import { Component } from '@theme/component';

/**
 * D-Pack Products mega panel.
 *
 * The element is rendered hidden inside the dpack-products-panel section
 * (sibling of the Horizon header section). The Products link in Horizon's
 * top nav carries `data-dpack-products-trigger="true"`; clicking it toggles
 * the panel. Categories cascade on pointerenter/focus to swap the three
 * right-hand columns (subs / detail / rail).
 *
 * @extends {Component}
 */
class DpackProductsMenu extends Component {
  #abortController = new AbortController();
  /** @type {HTMLElement | null} */
  #trigger = null;
  /** @type {number | null} rAF handle that throttles reposition work. */
  #rafId = null;

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#abortController;

    this.#trigger = /** @type {HTMLElement | null} */ (
      document.querySelector('[data-dpack-products-trigger]')
    );

    if (this.#trigger) {
      this.#trigger.addEventListener(
        'click',
        (event) => {
          event.preventDefault();
          this.toggle();
        },
        { signal }
      );
    }

    window.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.#isOpen()) this.close();
      },
      { signal }
    );

    document.addEventListener(
      'click',
      (event) => {
        if (!this.#isOpen()) return;
        if (!(event.target instanceof Node)) return;
        if (this.contains(event.target)) return;
        if (this.#trigger && this.#trigger.contains(event.target)) return;
        this.close();
      },
      { signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#stopTracking();
  }

  /** @returns {HTMLElement | null} */
  #panel() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.dp-mega-wrap'));
  }

  /** @returns {boolean} */
  #isOpen() {
    return this.#panel()?.getAttribute('data-open') === 'true';
  }

  /** Toggle open/close. */
  toggle() {
    if (this.#isOpen()) this.close();
    else this.open();
  }

  /** Open the panel. */
  open() {
    const panel = this.#panel();
    if (!panel) return;
    // Pin to the header's live position BEFORE revealing, so the panel never
    // flashes at the wrong offset. Bail if the header isn't on-screen to
    // anchor to (e.g. a non-sticky header already scrolled past).
    if (!this.#positionPanel()) return;
    panel.setAttribute('data-open', 'true');
    if (this.#trigger) this.#trigger.setAttribute('aria-expanded', 'true');
    this.#startTracking();
  }

  /**
   * Close the panel.
   * Bound via the scrim's `on:click="/close"`.
   */
  close() {
    const panel = this.#panel();
    if (!panel) return;
    panel.setAttribute('data-open', 'false');
    // Drop the inline overrides so the CSS fallback stays authoritative
    // between opens (the panel is re-pinned on every open anyway).
    panel.style.removeProperty('top');
    panel.style.removeProperty('--dp-top');
    this.#stopTracking();
    if (this.#trigger) {
      this.#trigger.setAttribute('aria-expanded', 'false');
      // Drop focus from the Products button so its focus ring isn't left
      // visible after ESC / scrim click / outside click.
      this.#trigger.blur();
    }
  }

  /**
   * Pin the (position: fixed) panel to the bottom edge of the header that
   * currently sits above it. Horizon's header is `position: sticky`, so that
   * edge lives at a different viewport offset depending on scroll position.
   * Measuring it live — instead of anchoring the panel to its own section in
   * normal document flow — keeps the dropdown flush under the header whether
   * the page is at the top or scrolled far down.
   *
   * Assumes a sticky, opaque header (the store's `enable_sticky_header:
   * 'always'`). When the header is not sticky and has scrolled out of view its
   * bottom edge goes <= 0; rather than stranding the panel at the top of the
   * viewport we report failure so the caller can close instead.
   *
   * @returns {boolean} `true` when the header is on-screen and the panel was
   * pinned to it; `false` when there is nothing on-screen to anchor to.
   */
  #positionPanel() {
    const panel = this.#panel();
    if (!panel) return false;
    const bottom = this.#headerBottom();
    if (bottom <= 0) return false;
    panel.style.top = `${bottom}px`;
    // Publish the live offset so CSS can cap the panel to the space actually
    // left below the header (see .dp-mega max-block-size).
    panel.style.setProperty('--dp-top', `${bottom}px`);
    return true;
  }

  /**
   * @returns {number} The viewport Y coordinate of the header's bottom edge.
   * May be <= 0 when a non-sticky header has scrolled out of view.
   */
  #headerBottom() {
    // Resolve the header section explicitly (order-independent) so an extra
    // section between the header and this panel can't mis-anchor the menu.
    const header =
      this.closest('#header-group')?.querySelector('.header-section') ??
      document.querySelector('.header-section');
    if (!(header instanceof HTMLElement)) return 0;
    return header.getBoundingClientRect().bottom;
  }

  /** Keep the panel glued to the header while it scrolls / the page resizes. */
  #startTracking() {
    const { signal } = this.#abortController;
    // addEventListener de-dupes identical (type, listener) pairs, so repeated
    // opens never stack listeners; #stopTracking removes them on close.
    window.addEventListener('scroll', this.#onViewportChange, { passive: true, signal });
    window.addEventListener('resize', this.#onViewportChange, { passive: true, signal });
  }

  #stopTracking() {
    window.removeEventListener('scroll', this.#onViewportChange);
    window.removeEventListener('resize', this.#onViewportChange);
    if (this.#rafId !== null) {
      cancelAnimationFrame(this.#rafId);
      this.#rafId = null;
    }
  }

  /** rAF-throttled reposition handler (bound field so it can be removed). */
  #onViewportChange = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      if (!this.#isOpen()) return;
      // Re-pin to the header; if it has scrolled out of view, close rather
      // than leaving the panel stranded at the top of the viewport.
      if (!this.#positionPanel()) this.close();
    });
  };

  /**
   * Activate a category — swap the active state on the column-0 button
   * and on every column's per-category pane.
   * Bound via `on:pointerenter="/activateCat"` and `on:focus="/activateCat"`.
   * @param {Event} event
   */
  activateCat(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const id = button.dataset.catId;
    if (!id) return;
    if (this.dataset.activeCatId === id) return;

    this.dataset.activeCatId = id;

    this.querySelectorAll('.dp-cat').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-cat-id') === id);
    });
    this.querySelectorAll('[data-cat-pane]').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-cat-pane') === id);
    });
  }

  /**
   * Activate a subcategory inside the active category's detail pane.
   * Bound via `on:pointerenter="/activateSub"` and `on:focus="/activateSub"`.
   * @param {Event} event
   */
  activateSub(event) {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const subId = button.dataset.subId;
    const catId = button.dataset.catId;
    if (!subId || !catId) return;

    // Scope to the active category's subs pane + detail pane only.
    const subsPane = this.querySelector(
      `.dp-mega__subs > [data-cat-pane="${catId}"]`
    );
    const detailPane = this.querySelector(
      `.dp-mega__detail > [data-cat-pane="${catId}"]`
    );
    if (!subsPane || !detailPane) return;

    subsPane.querySelectorAll('[data-sub-id]').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-sub-id') === subId);
    });
    detailPane.querySelectorAll('[data-sub-pane]').forEach((el) => {
      el.classList.toggle('is-active', el.getAttribute('data-sub-pane') === subId);
    });
  }
}

/**
 * D-Pack mobile drawer — 3-level slide flow (Site rows → Categories → Subs → Sub-subs).
 *
 * Lives INSIDE Horizon's <header-drawer> so the open/close (hamburger
 * tap) is still driven by Horizon's existing logic. This component
 * only manages the slide-pane state via data-view + data-level
 * attributes plus per-group `.is-active` toggles.
 *
 * @extends {Component}
 */
class DpackMobileDrawer extends Component {
  /** @type {string | null} */
  #catId = null;
  /** @type {string | null} */
  #subId = null;

  connectedCallback() {
    super.connectedCallback();
    // Initial state already reflected by data-view / data-level on host.
    this.#syncBackButton();
  }

  /**
   * Drill into the Products category list (pane 1).
   * Bound via `on:click="/goProducts"`.
   * @param {Event} event
   */
  goProducts(event) {
    event.preventDefault();
    this.dataset.view = 'products';
    this.dataset.level = '0';
    this.#syncBackButton();
  }

  /**
   * Drill into a category's subcategory list (pane 2).
   * Bound via `on:click="/goCategory"`.
   * @param {Event} event
   */
  goCategory(event) {
    event.preventDefault();
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    this.#catId = button.dataset.catId ?? null;
    this.dataset.level = '1';
    this.#syncGroups();
    this.#syncBackButton();
  }

  /**
   * Drill into a sub's child list (pane 3).
   * Bound via `on:click="/goSub"`.
   * @param {Event} event
   */
  goSub(event) {
    event.preventDefault();
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    this.#subId = button.dataset.subId ?? null;
    this.dataset.level = '2';
    this.#syncGroups();
    this.#syncBackButton();
  }

  /**
   * Step back one level.
   * Bound via `on:click="/back"`.
   */
  back() {
    const level = parseInt(this.dataset.level ?? '0', 10);
    if (level === 2) {
      this.#subId = null;
      this.dataset.level = '1';
    } else if (level === 1) {
      this.#catId = null;
      this.dataset.level = '0';
    } else if (this.dataset.view === 'products') {
      this.dataset.view = 'site';
    }
    this.#syncGroups();
    this.#syncBackButton();
  }

  #syncGroups() {
    this.querySelectorAll('[data-sub-group]').forEach((el) => {
      el.classList.toggle(
        'is-active',
        el.getAttribute('data-sub-group') === this.#catId
      );
    });
    this.querySelectorAll('[data-kids-group]').forEach((el) => {
      el.classList.toggle(
        'is-active',
        el.getAttribute('data-kids-group') === this.#subId
      );
    });
  }

  #syncBackButton() {
    const back = /** @type {HTMLElement | null} */ (
      this.querySelector('.dp-drawer__back')
    );
    const title = /** @type {HTMLElement | null} */ (
      this.querySelector('.dp-drawer__title')
    );
    const label = /** @type {HTMLElement | null} */ (
      this.querySelector('.dp-drawer__back-label')
    );
    if (!back || !title) return;

    const view = this.dataset.view;
    const level = parseInt(this.dataset.level ?? '0', 10);

    const showBack = view === 'products' || level > 0;
    back.classList.toggle('is-hidden', !showBack);

    if (label) {
      if (level === 2) label.textContent = 'Subcategories';
      else if (level === 1) label.textContent = 'Categories';
      else label.textContent = 'Menu';
    }
  }
}

if (!customElements.get('dpack-products-menu')) {
  customElements.define('dpack-products-menu', DpackProductsMenu);
}
if (!customElements.get('dpack-mobile-drawer')) {
  customElements.define('dpack-mobile-drawer', DpackMobileDrawer);
}
